import { Feature } from 'ol';
import type { Coordinate } from 'ol/coordinate';
import type { FeatureLike } from 'ol/Feature.js';
import type { Geometry } from 'ol/geom';
import { Point, Polygon } from 'ol/geom';
import type { ModifyEvent } from 'ol/interaction/Modify';
import Modify from 'ol/interaction/Modify';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import type { Style } from 'ol/style';

import { DrawToolType } from '@nora-monorepo/nora-api-client';

import { CreateFeatureBaseDrawTool } from '#nora-ui/map/drawtools/create-feature-base-draw-tool';
import { getHandleModifyPixelTolerance } from '#nora-ui/map/drawtools/draw-tool-utils';
import { getLineHandleStyle, getMidpointHandleStyle } from '#nora-ui/map/drawtools/styles/straight-line-style';
import { getFeatureStyle } from '#nora-ui/map/drawtools/styles/styles';
import type { DrawToolOptions } from '#nora-ui/map/drawtools/types';
import { extractDrawToolOptions } from '#nora-ui/map/drawtools/types';
import type { DrawToolManager } from '#nora-ui/map/map/DrawToolManager';

type VertexHandleType = 'vertex';
type MidpointHandleType = 'midpoint';
type CenterHandleType = 'center-handle';
type HandleType = VertexHandleType | MidpointHandleType | CenterHandleType;

export class PolygonTool extends CreateFeatureBaseDrawTool {
  override drawToolType: DrawToolType = DrawToolType.polygon;
  override options: DrawToolOptions = {
    drawToolType: this.drawToolType,
  };

  private readonly modifyInteraction: Modify;
  private readonly handleSource: VectorSource;
  private readonly handleLayer: VectorLayer<VectorSource>;

  private polygonFeature!: Feature<Polygon>;
  private isInitialized = false;

  private currentHandleType: HandleType | null = null;

  private centerHandle!: Feature<Point>;
  private previousCenterHandleCoordinate: Coordinate | null = null;

  private handleChangeListener: (() => void) | null = null;
  private modifiedHandleFeature: Feature<Geometry> | null = null;

  constructor(drawToolManager: DrawToolManager) {
    super(drawToolManager);
    this.drawToolName = 'Polygon';

    this.handleSource = new VectorSource();
    this.handleLayer = new VectorLayer<VectorSource>({
      source: this.handleSource,
      style: this.determineHandleStyle.bind(this),
      zIndex: 10000,
    });

    this.modifyInteraction = new Modify({
      source: this.handleSource,
      insertVertexCondition: () => false,
      pixelTolerance: getHandleModifyPixelTolerance(),
    });

    this.modifyInteraction.on('modifystart', this.onModifyStart.bind(this));
    this.modifyInteraction.on('modifyend', this.onModifyEnd.bind(this));
  }

  private determineHandleStyle(feature: FeatureLike): Style {
    const handleType = feature.get('handleType') as HandleType | undefined;

    if (handleType === 'midpoint') {
      return getMidpointHandleStyle();
    }

    return getLineHandleStyle();
  }

  private onModifyStart(event: ModifyEvent): void {
    this.handleLayer.setVisible(false);
    const features = event.features.getArray();

    if (features.length === 0) {
      return;
    }

    const handleFeature = features.at(0);

    if (!handleFeature) {
      return;
    }

    const handleType = handleFeature.get('handleType') as
      | HandleType
      | undefined;

    if (!handleType) {
      return;
    }

    if (handleType === 'center-handle') {
      this.currentHandleType = handleType;
      const handleGeometry = handleFeature.getGeometry() as Point;
      const handleCoordinates = handleGeometry.getCoordinates();

      this.previousCenterHandleCoordinate = handleCoordinates;
      this.modifiedHandleFeature = handleFeature;
      const geometry = handleGeometry;
      const listener = (): void => {
        this.updatePolygonTransform();
      };

      geometry.on('change', listener);

      this.handleChangeListener = listener;

      return;
    }

    if (handleType === 'midpoint') {
      const segmentIndex = handleFeature.get('segmentIndex') as number | undefined;

      if (segmentIndex === undefined) {
        return;
      }

      this.insertVertexFromMidpointHandle(handleFeature, segmentIndex);
      const newHandleType = handleFeature.get('handleType') as
        | HandleType
        | undefined;

      if (newHandleType === 'vertex') {
        this.currentHandleType = newHandleType;
        this.modifiedHandleFeature = handleFeature;

        const geometry = handleFeature.getGeometry() as Point;
        const listener = (): void => {
          this.updatePolygonFromHandleModification();
        };
        geometry.on('change', listener);
        this.handleChangeListener = listener;
      }
    }
    else if (handleType === 'vertex') {
      this.currentHandleType = handleType;
      this.modifiedHandleFeature = handleFeature;

      const geometry = handleFeature.getGeometry() as Point;
      const listener = (): void => {
        this.updatePolygonFromHandleModification();
      };
      geometry.on('change', listener);
      this.handleChangeListener = listener;
    }
  }

  private onModifyEnd(): void {
    this.handleLayer.setVisible(true);

    if (this.handleChangeListener && this.modifiedHandleFeature) {
      const geometry = this.modifiedHandleFeature.getGeometry() as Point;
      geometry.un('change', this.handleChangeListener);
    }

    this.handleChangeListener = null;
    this.modifiedHandleFeature = null;
    this.currentHandleType = null;
    this.previousCenterHandleCoordinate = null;

    this.addPolygonHandles();
    this.markDirty();
  }

  override activate(): void {
    super.activate();
    const map = this.drawToolManager.map.getOlMapOrThrow();

    map.addLayer(this.handleLayer);
    map.addInteraction(this.modifyInteraction);

    if (!this.isInitialized) {
      this.createInitialPolygon();
      this.isInitialized = true;
    }

    this.addPolygonHandles();
    this.handleLayer.setVisible(true);
  }

  override deactivate(): void {
    super.deactivate();
    const map = this.drawToolManager.map.getOlMapOrThrow();
    map.removeInteraction(this.modifyInteraction);
    this.handleSource.clear();
    this.handleLayer.setVisible(false);
    map.removeLayer(this.handleLayer);
  }

  private createInitialPolygon(): void {
    const map = this.drawToolManager.map.getOlMapOrThrow();
    const view = map.getView();
    const centerCoordinates = view.getCenter() as [number, number] | undefined;

    if (!centerCoordinates) {
      return;
    }

    const mapSize = map.getSize();

    if (!mapSize) {
      return;
    }

    const [width, height] = mapSize;

    if (width === undefined || height === undefined) {
      return;
    }

    const resolution = view.getResolution() ?? 1;
    const halfSize = (Math.min(width, height) / 8) * resolution;

    // initial triangle shape
    const coordinates: [number, number][] = [
      [centerCoordinates[0], centerCoordinates[1] - halfSize],
      [centerCoordinates[0] + halfSize, centerCoordinates[1] + halfSize],
      [centerCoordinates[0] - halfSize, centerCoordinates[1] + halfSize],
      [centerCoordinates[0], centerCoordinates[1] - halfSize],
    ];
    const polygonGeometry = new Polygon([coordinates]);

    this.polygonFeature = new Feature<Polygon>(polygonGeometry);
    this.polygonFeature.setProperties(this.getDefaultProperties());
    this.polygonFeature.setStyle(getFeatureStyle);

    this.editingVectorSource.clear();
    this.editingVectorSource.addFeature(this.polygonFeature);
  }

  /**
   * Rebuilds all editing handles: vertex, midpoint, and the center (move) handle.
   */
  private addPolygonHandles(): void {
    this.handleSource.clear();

    const polygonGeometry = this.polygonFeature.getGeometry() as Polygon;
    const coordinates = polygonGeometry.getCoordinates()[0];

    // add handles, but exclude last coordinate since it's a duplicate of the first
    coordinates.slice(0, -1).forEach((coordinate, index) => {
      const vertexFeature = new Feature<Point>(new Point(coordinate));
      vertexFeature.set('handleType', 'vertex');
      vertexFeature.set('vertexIndex', index);
      this.handleSource.addFeature(vertexFeature);
    });

    // add midpoint handles between each vertex
    for (
      let segmentIndex = 0;
      segmentIndex < coordinates.length - 1;
      segmentIndex++
    ) {
      const startCoordinate = coordinates[segmentIndex];
      const endCoordinate = coordinates[segmentIndex + 1];

      const [startX, startY] = startCoordinate;
      const [endX, endY] = endCoordinate;

      if (
        startX === undefined
        || startY === undefined
        || endX === undefined
        || endY === undefined
      ) {
        continue;
      }

      const midpointCoordinate: [number, number] = [
        (startX + endX) / 2,
        (startY + endY) / 2,
      ];
      const midpointFeature = new Feature<Point>(new Point(midpointCoordinate));
      midpointFeature.set('handleType', 'midpoint');
      midpointFeature.set('segmentIndex', segmentIndex);
      this.handleSource.addFeature(midpointFeature);
    }

    this.addCenterHandle();
  }

  /**
   * Computes a center point for the polygon (using the average of the vertices)
   * and adds a new center handle for moving the entire polygon.
   */
  private addCenterHandle(): void {
    const polygonGeometry = this.polygonFeature.getGeometry() as Polygon;
    const coords = polygonGeometry.getCoordinates()[0];

    if (coords.length < 3) {
      return;
    }

    let sumX = 0;
    let sumY = 0;
    let count = 0;
    for (let i = 0; i < coords.length - 1; i++) {
      const coordinate = coords[i];

      const [coordinateX, coordinateY] = coordinate;

      if (coordinateX === undefined || coordinateY === undefined) {
        return;
      }

      sumX += coordinateX;
      sumY += coordinateY;
      count++;
    }
    const center: [number, number] = [sumX / count, sumY / count];
    this.centerHandle = new Feature<Point>(new Point(center));
    this.centerHandle.set('handleType', 'center-handle');
    this.handleSource.addFeature(this.centerHandle);
  }

  /**
   * Called when the center handle is dragged. Computes the delta movement
   * and applies that offset to every vertex of the polygon.
   */
  private updatePolygonTransform(): void {
    if (!this.modifiedHandleFeature || !this.previousCenterHandleCoordinate) {
      return;
    }

    const newCenter = (
      this.modifiedHandleFeature.getGeometry() as Point
    ).getCoordinates();

    const [centerX, centerY] = newCenter;
    const [previousCenterX, previousCenterY] = this.previousCenterHandleCoordinate;

    if (
      centerX === undefined
      || centerY === undefined
      || previousCenterX === undefined
      || previousCenterY === undefined
    ) {
      return;
    }

    const dx = centerX - previousCenterX;
    const dy = centerY - previousCenterY;

    const polygonGeometry = this.polygonFeature.getGeometry() as Polygon;
    const ring = polygonGeometry.getCoordinates()[0];

    const newRing = ring.map((coordinate) => {
      const [coordinateX, coordinateY] = coordinate;

      if (coordinateX === undefined || coordinateY === undefined) {
        return [0, 0] as [number, number];
      }

      return [
        coordinateX + dx,
        coordinateY + dy,
      ];
    });

    const firstCoordinate = newRing[0];

    newRing[newRing.length - 1] = firstCoordinate;
    polygonGeometry.setCoordinates([newRing]);
    this.polygonFeature.changed();

    this.previousCenterHandleCoordinate = newCenter;
    this.markDirty();
  }

  /**
   * Inserts a new vertex into the Polygon from a midpoint handle.
   */
  private insertVertexFromMidpointHandle(
    midpointHandle: Feature<Geometry>,
    segmentIndex: number,
  ): void {
    const polygonGeometry = this.polygonFeature.getGeometry() as Polygon;
    const coordinates = polygonGeometry.getCoordinates()[0];

    const midpointCoordinate = (
      midpointHandle.getGeometry() as Point
    ).getCoordinates() as [number, number];

    coordinates.splice(segmentIndex + 1, 0, midpointCoordinate);
    const firstCoordinate = coordinates[0];

    coordinates[coordinates.length - 1] = firstCoordinate;
    polygonGeometry.setCoordinates([coordinates]);
    this.polygonFeature.changed();

    midpointHandle.set('handleType', 'vertex');
    midpointHandle.unset('segmentIndex');

    const newVertexIndex = segmentIndex + 1;
    this.handleSource.getFeatures().forEach((feature) => {
      if (feature === midpointHandle) {
        feature.set('vertexIndex', newVertexIndex);
        return;
      }

      if (feature.get('handleType') === 'vertex') {
        const existingIndex = feature.get('vertexIndex') as number;

        if (existingIndex >= newVertexIndex) {
          feature.set('vertexIndex', existingIndex + 1);
        }
      }
    });

    this.currentHandleType = 'vertex';
    const geometry = midpointHandle.getGeometry() as Point;
    const listener = (): void => {
      this.updatePolygonFromHandleModification();
    };
    geometry.on('change', listener);
    this.handleChangeListener = listener;
  }

  /**
   * Updates the Polygon geometry based on the modification of a vertex handle.
   */
  private updatePolygonFromHandleModification(): void {
    if (!this.currentHandleType || !this.modifiedHandleFeature) {
      return;
    }

    const polygonGeometry = this.polygonFeature.getGeometry() as Polygon;
    const coordinates = polygonGeometry.getCoordinates()[0];

    if (this.currentHandleType === 'vertex') {
      const index = this.modifiedHandleFeature.get('vertexIndex') as number | undefined;

      if (index === undefined) {
        return;
      }

      const newCoordinate = (
        this.modifiedHandleFeature.getGeometry() as Point
      ).getCoordinates() as [number, number];

      if (index >= coordinates.length) {
        return;
      }

      coordinates[index] = newCoordinate;
      const firstCoordinate = coordinates[0];

      coordinates[coordinates.length - 1] = firstCoordinate;
    }
    polygonGeometry.setCoordinates([coordinates]);
    this.polygonFeature.changed();
    this.markDirty();
  }

  override async save(): Promise<void> {
    if (!this.isInitialized) {
      return;
    }

    this.feature = this.polygonFeature;

    await super.save();
  }

  override onStylingUpdated(): void {
    super.onStylingUpdated();
    this.polygonFeature.setProperties(this.getDefaultProperties());
    this.polygonFeature.setStyle(getFeatureStyle);
    this.markDirty();
  }

  override showFillColor(): boolean {
    return true;
  }

  override loadFeature(feature: Feature<Geometry>): boolean {
    if (Array.isArray(feature)) {
      return false;
    }

    const geometry = feature.getGeometry();

    if (!(geometry instanceof Polygon)) {
      return false;
    }

    const featureProperties = extractDrawToolOptions(feature);
    this.drawToolManager.updateDrawStyleOptions(featureProperties);

    const coordinates = geometry.getCoordinates();

    if (coordinates.length === 0) {
      return false;
    }

    const newPolygonGeometry = new Polygon(coordinates);

    const featureId = feature.get('id') as string | undefined;

    const loadedPolygonFeature = new Feature<Polygon>(newPolygonGeometry);
    loadedPolygonFeature.setProperties({
      ...this.getDefaultProperties(),
      ...featureProperties,
      id: featureId,
    });
    loadedPolygonFeature.setStyle(getFeatureStyle);

    this.featureId = featureId;
    this.polygonFeature = loadedPolygonFeature;
    this.isInitialized = true;
    this.addPolygonHandles();
    super.loadFeature(loadedPolygonFeature);

    return true;
  }
}
